import {
  columnsNames,
  hvacColumnsNames,
  ambientColumnsNames,
} from "./constant";
import { Chip } from "@mui/material";
import { styled } from "@mui/styles";
import CustomSelectEditor from "./CustomSelects";
import "../remediationDocumentStyles.css";
import SelectDropDown from "main/components/SelectDropdown/SelectDropDown";
import CustomFilters from "main/components/CustomFilters/CustomSetFilter";
import labResultInActive from "assets/icons/labResultInActive.svg";
import labResultActive from "assets/icons/labResultActive.svg";

// Helper function to check if lab result icon should be shown
const shouldShowLabResultIcon = (tableKey, testName) => {
  return (
    tableKey === "remediation_report" &&
    testName &&
    (testName.toLowerCase().includes("air") ||
      testName.toLowerCase().includes("swab"))
  );
};

export const compareSampleReports = (
  oldReport,
  newReport,
  isDragged = false
) => {
  const reportMap = new Map(oldReport.map((item) => [item.id, item]));

  const roomPositions = new Map();
  oldReport.forEach((item, index) => {
    if (!roomPositions.has(item.room_name.name)) {
      roomPositions.set(item.room_name.name, index);
    }
  });

  newReport.forEach((newItem) => {
    if (reportMap.has(newItem.id)) {
      const existingItem = reportMap.get(newItem.id);

      // Update observation.name, tier, and result if changed
      if (existingItem.observation.name !== newItem.observation.name) {
        existingItem.observation.name = newItem.observation.name;
      }

      if (existingItem.room_name?.name !== newItem.room_name?.name) {
        existingItem.room_name.name = newItem.room_name.name;
      }

      if (existingItem.tier !== newItem.tier) {
        existingItem.tier = newItem.tier;
      }

      if (existingItem.status !== newItem.status) {
        existingItem.status = newItem.status;
      }

      if (existingItem.result !== newItem.result) {
        existingItem.result = newItem.result;
      }
    } else {
      const roomIndex = roomPositions.get(newItem.room_name.name);
      if (roomIndex !== undefined) {
        oldReport.splice(roomIndex + 1, 0, newItem);
      } else {
        oldReport.push(newItem);
      }
      reportMap.set(newItem.id, newItem);
    }
  });

  // Remove deleted rows
  const newIds = new Set(newReport.map((item) => item.id));
  Array.from(reportMap.keys()).forEach((id) => {
    if (!newIds.has(id)) {
      reportMap.delete(id);
    }
  });

  if (isDragged) {
    return Array.from(reportMap.values());
  }

  // Group by room
  const grouped = Array.from(reportMap.values()).reduce((acc, item) => {
    const room = item.room_name.name;
    if (!acc[room]) acc[room] = [];
    acc[room].push(item);
    return acc;
  }, {});

  // Sort by observation name order in oldReport
  const sorted = Object.keys(grouped).reduce((res, room) => {
    const items = grouped[room];
    const sortedItems = items.sort((a, b) => {
      const indexA = oldReport.findIndex(
        (i) => i.observation.name === a.observation.name
      );
      const indexB = oldReport.findIndex(
        (i) => i.observation.name === b.observation.name
      );
      return (
        (indexA === -1 ? Infinity : indexA) -
        (indexB === -1 ? Infinity : indexB)
      );
    });
    res.push(...sortedItems);
    return res;
  }, []);

  return sorted;
};

export const compareHvacReports = (oldReport, newReport, isDragged = false) => {
  const reportMap = new Map(oldReport.map((item) => [item.id, item]));

  const roomPositions = new Map();
  oldReport.forEach((item, index) => {
    if (!roomPositions.has(item.room_name.name)) {
      roomPositions.set(item.room_name.name, index);
    }
  });

  newReport.forEach((newItem) => {
    if (reportMap.has(newItem.id)) {
      const existingItem = reportMap.get(newItem.id);

      // Update room name if changed
      if (existingItem.room_name.name !== newItem.room_name.name) {
        existingItem.room_name.name = newItem.room_name.name;
      }

      if (existingItem.tier !== newItem.tier) {
        existingItem.tier = newItem.tier;
      }

      if (existingItem.result !== newItem.result) {
        existingItem.result = newItem.result;
      }
    } else {
      const roomIndex = roomPositions.get(newItem.room_name.name);
      if (roomIndex !== undefined) {
        oldReport.splice(roomIndex + 1, 0, newItem);
      } else {
        oldReport.push(newItem);
      }
      reportMap.set(newItem.id, newItem);
    }
  });

  // Remove deleted items
  const newIds = new Set(newReport.map((item) => item.id));
  Array.from(reportMap.keys()).forEach((id) => {
    if (!newIds.has(id)) {
      reportMap.delete(id);
    }
  });

  if (isDragged) {
    return Array.from(reportMap.values());
  }

  // Group by room name
  const grouped = Array.from(reportMap.values()).reduce((acc, item) => {
    const room = item.room_name.name;
    if (!acc[room]) acc[room] = [];
    acc[room].push(item);
    return acc;
  }, {});

  // Sort room groups alphabetically and flatten
  const sorted = Object.keys(grouped)
    .sort() // alphabetical sort by room name
    .reduce((res, room) => {
      res.push(...grouped[room]);
      return res;
    }, []);

  return sorted;
};

export const comparAmbientReports = (
  oldReport,
  newReport,
  isDragged = false
) => {
  const reportMap = new Map(oldReport.map((item) => [item.id, item]));

  const roomPositions = new Map();
  oldReport.forEach((item, index) => {
    if (!roomPositions.has(item.room_name.name)) {
      roomPositions.set(item.room_name.name, index);
    }
  });

  newReport.forEach((newItem) => {
    if (reportMap.has(newItem.id)) {
      const existingItem = reportMap.get(newItem.id);

      // Update room name if changed
      if (existingItem.room_name.name !== newItem.room_name.name) {
        existingItem.room_name.name = newItem.room_name.name;
      }

      if (existingItem.tier !== newItem.tier) {
        existingItem.tier = newItem.tier;
      }

      if (existingItem.result !== newItem.result) {
        existingItem.result = newItem.result;
      }
    } else {
      const roomIndex = roomPositions.get(newItem.room_name.name);
      if (roomIndex !== undefined) {
        oldReport.splice(roomIndex + 1, 0, newItem);
      } else {
        oldReport.push(newItem);
      }
      reportMap.set(newItem.id, newItem);
    }
  });

  // Remove deleted items
  const newIds = new Set(newReport.map((item) => item.id));
  Array.from(reportMap.keys()).forEach((id) => {
    if (!newIds.has(id)) {
      reportMap.delete(id);
    }
  });

  if (isDragged) {
    return Array.from(reportMap.values());
  }

  // Group by room name
  const grouped = Array.from(reportMap.values()).reduce((acc, item) => {
    const room = item.room_name.name;
    if (!acc[room]) acc[room] = [];
    acc[room].push(item);
    return acc;
  }, {});

  // Sort room groups alphabetically and flatten
  const sorted = Object.keys(grouped)
    .sort() // alphabetical sort by room name
    .reduce((res, room) => {
      res.push(...grouped[room]);
      return res;
    }, []);

  return sorted;
};

export const compareReportData = (
  oldData,
  newData,
  options = {
    fieldsToCompare: [],
    sortByField: null,
    isDragged: false,
    groupByRoom: true,
  }
) => {
  const { fieldsToCompare, sortByField, isDragged, groupByRoom } = options;

  const reportMap = new Map(oldData.map((item) => [item.id, item]));

  // Store original room positions
  const roomPositions = new Map();
  oldData.forEach((item, index) => {
    const roomName = item.room_name?.name;
    if (roomName && !roomPositions.has(roomName)) {
      roomPositions.set(roomName, index);
    }
  });

  newData.forEach((newItem) => {
    if (reportMap.has(newItem.id)) {
      const existingItem = reportMap.get(newItem.id);

      fieldsToCompare.forEach((field) => {
        const oldValue = field
          .split(".")
          .reduce((obj, key) => obj?.[key], existingItem);
        const newValue = field
          .split(".")
          .reduce((obj, key) => obj?.[key], newItem);

        if (oldValue !== newValue) {
          let ref = existingItem;
          const parts = field.split(".");
          parts.forEach((key, idx) => {
            if (idx === parts.length - 1) {
              ref[key] = newValue;
            } else {
              ref = ref[key];
            }
          });
        }
      });
    } else {
      const roomIndex = roomPositions.get(newItem.room_name?.name);
      if (roomIndex !== undefined) {
        oldData.splice(roomIndex + 1, 0, newItem);
      } else {
        oldData.push(newItem);
      }
      reportMap.set(newItem.id, newItem);
    }
  });

  // Remove deleted rows
  const newIds = new Set(newData.map((item) => item.id));
  for (const id of reportMap.keys()) {
    if (!newIds.has(id)) {
      reportMap.delete(id);
    }
  }

  const finalList = Array.from(reportMap.values());

  if (isDragged) return finalList;

  if (!groupByRoom) return finalList;

  // Group by room name
  const grouped = finalList.reduce((acc, item) => {
    const room = item.room_name?.name;
    if (!acc[room]) acc[room] = [];
    acc[room].push(item);
    return acc;
  }, {});

  // Sort based on a field (if provided)
  const sorted = Object.keys(grouped).reduce((res, room) => {
    let items = grouped[room];
    if (sortByField) {
      items = items.sort((a, b) => {
        const aValue = sortByField
          .split(".")
          .reduce((obj, key) => obj?.[key], a);
        const bValue = sortByField
          .split(".")
          .reduce((obj, key) => obj?.[key], b);
        return aValue?.localeCompare?.(bValue) ?? 0;
      });
    }
    res.push(...items);
    return res;
  }, []);

  return sorted;
};

export const CustomTestDropDown = (
  options,
  onUpdate,
  props,
  rowIndex,
  data
) => {
  return (
    <SelectDropDown
      id={rowIndex}
      placeholder="Select .."
      options={options}
      handleOnChange={(e) => {
        const updatedTest = {
          id: e.value,
          name: e.label,
        };
        onUpdate(updatedTest);
      }}
      searchValue={options.find(
        (option) => Number(option.value) === Number(props.defaultValue.id)
      )}
      isSearchable={false}
      isClearable={false}
    />
  );
};

export const CustomReasonDropDown = (
  options,
  onUpdate,
  props,
  rowIndex,
  data
) => {
  return (
    <SelectDropDown
      placeholder="Select .."
      options={options}
      handleOnChange={(e) => {
        const updatedReason = {
          id: e.value,
          name: e.label,
        };
        onUpdate(updatedReason);
      }}
      searchValue={options.find(
        (option) => Number(option.value) === Number(props.defaultValue.id)
      )}
      isSearchable={false}
      isClearable={false}
    />
  );
};

export const getNestedValue = (obj, path) => {
  const keys = path.split(".");
  return keys.reduce(
    (acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
    obj
  );
};

export const setNestedValue = (obj, path, value) => {
  const keys = path.split(".");
  keys.reduce((acc, key, index) => {
    if (index === keys.length - 1) {
      acc[key] = value;
    }
    return acc[key] !== undefined ? acc[key] : (acc[key] = {});
  }, obj);
};

const handleRoomUpdateLogic = (row, data) => {
  let updatedRoom = "";
  updatedRoom = row?.room_name;

  data.forEach((li) => {
    if (li.room_name?.id === updatedRoom.id) {
      li.room_name = JSON.parse(JSON.stringify(updatedRoom));
    }
  });
};

const handleObservationUpdateLogic = (row, data) => {
  let updatedObservation = "";
  updatedObservation = row?.observation;
  data.forEach((li) => {
    if (li.observation_id === row.observation_id) {
      li.observation = JSON.parse(JSON.stringify(updatedObservation));
    }
  });
};

const handleTestUpdateLogic = (row, data, testList) => {
  const selectedTest = row?.test;
  const selectedTestSampleCost = testList.find(
    (test) => Number(test.id) === Number(selectedTest.id)
  );
  if (
    selectedTestSampleCost &&
    selectedTestSampleCost?.test_samples.length > 0
  ) {
    const cost = selectedTestSampleCost?.test_samples[0]?.cost;
    const sample_id = selectedTestSampleCost?.test_samples[0]?.id;
    data.forEach((li) => {
      if (li?.id === row.id) {
        li.test.sample_id = sample_id;
        li.test_sample.cost = cost;
      }
    });
  }
};

const ApprovedChip = styled(Chip)({
  color: "#5DC346",
  borderColor: "#5DC346",
  backgroundColor: "#E7F8E3",
  borderRadius: 4,
  fontSize: "10px",
  fontWeight: 500,
});

const UnapprovedChip = styled(Chip)({
  color: "#ED1B23",
  borderColor: "#ED1B23",
  backgroundColor: "#FFF0F0",
  borderRadius: 4,
  fontSize: "10px",
  fontWeight: 500,
});

const sharedColumns = {
  selection: ({
    tableKey,
    selectAllRows,
    handleSelectRow,
    handleAllSelectRow,
  }) => ({
    //headerComponent: (event) => <SnackBar event={event} />,
    headerName: "Sr",
    valueGetter: (params) => params.node.rowIndex + 1,
    editable: false,
    width: 40,
    filter: false,
    sortable: false,
    suppressMovable: true,
    onCellClicked: (event) => {
      const isSelected = event.node.isSelected();
      event.node.setSelected(!isSelected);
    },

    cellStyle: { textAlign: "center" },
  }),
  drag: {
    headerName: "",
    field: "drag",
    rowDrag: true,
    rowDragMultiRow: true,
    width: 50,
    sortable: false,
    filter: false,
    suppressMovable: true,
    suppressMoveWhenRowDragging: true,
  },
  roomName: ({ data, columnNames }) => ({
    headerName: columnNames?.room?.name,
    field: columnNames?.room?.value,
    editable: true,
    flex: 2,
    minWidth: 130,
    cellEditorParams: { type: "text", rows: 4, maxLength: 50 },
    cellStyle: { textAlign: "left" },
    valueFormatter: (params) => params.value || "-",
    filter: "agTextColumnFilter",
    filterParams: { filterOptions: ["contains"], maxNumConditions: 1 },
    sortable: false,
    suppressMovable: true,
    valueSetter: (params) => {
      if (params.newValue !== params.data.room_name?.name) {
        params.data.room_name = {
          ...params.data.room_name,
          name: params.newValue,
          edit_col: true,
        };
        handleRoomUpdateLogic(params.data, data);
        return true;
      }
      return false;
    },
  }),
  sampleNote: ({ columnNames }) => ({
    headerName: columnNames.sample_note.name,
    field: columnNames.sample_note.value,
    editable: true,
    cellEditorParams: { type: "text", rows: 4, maxLength: 50 },
    flex: 2,
    minWidth: 100,
    filter: false,
    sortable: false,
    suppressMovable: true,
  }),
  result: ({ columnNames }) => ({
    headerName: columnNames.result.name,
    field: columnNames.result.value,
    editable: true,
    flex: 2,
    cellEditorParams: { type: "text", rows: 4, maxLength: 50 },
    minWidth: 100,
    filter: false,
    sortable: false,
    suppressMovable: true,
  }),
  status: ({ columnNames, handleStatusRow, tableKey }) => ({
    headerName: columnNames.status.name,
    field: columnNames.status.value,
    editable: false,
    flex: 1,
    minWidth: 100,
    filter: false,
    sortable: false,
    suppressMovable: true,
    cellClass: "center-cell",
    cellRenderer: (params) => {
      const isApproved = params.value === true;
      return isApproved ? (
        <ApprovedChip
          label="Approved"
          onClick={() => handleStatusRow(params.data, tableKey)}
          variant="outlined"
          size="small"
        />
      ) : (
        <UnapprovedChip
          label="Unapproved"
          onClick={() => handleStatusRow(params.data, tableKey)}
          variant="outlined"
          size="small"
        />
      );
    },
  }),
  tier: ({ tiers, tableKey, selectedTier, setSelectedTier, columnNames }) => ({
    headerComponent: () => (
      <CustomFilters
        header="Tier"
        tableKey={tableKey}
        testList={tiers.tier.map((test) => test.prefix)}
        selectedOptions={selectedTier}
        setSelectedOptions={setSelectedTier}
      />
    ),
    headerName: columnNames.tier.name,
    field: columnNames.tier.value,
    editable: true,
    flex: 1,
    minWidth: 100,
    sortable: false,
    suppressMovable: true,
    cellEditor: CustomSelectEditor,
    cellEditorParams: { values: tiers.tier, tableKey: "tier" },
    valueGetter: (params) => params.data.tier || "",
    valueSetter: (params) => {
      const selectedTier = tiers.tier.find(
        (tier) => tier.prefix === params.newValue.prefix
      );
      if (selectedTier) {
        params.data.tier = selectedTier.prefix;
        return true;
      }
      return false;
    },
    cellClassRules: {
      "tier-a-class": (params) =>
        params.value === "A" ||
        params.value === "Basic Cleaning" ||
        params.value === "Deep Cleaning" ||
        params.value === "Full Pack Out" ||
        params.value === "Clean" ||
        params.value === "Remove" ||
        params.value === "Pending",
      "tier-b-class": (params) => params.value === "B",
      "tier-c-class": (params) => params.value === "C",
      "tier-d-class": (params) => params.value === "D",
      "tier-e-class": (params) => params.value === "E",
      "tier-i-class": (params) => params.value === "I",
      "tier-p-class": (params) => params.value === "P",
    },
  }),

  delete: ({ handleDeleteRow, tableKey, handleToggleSidebar }) => ({
    headerName: "",
    field: "",
    width: tableKey === "remediation_report" ? 80 : 55,
    filter: false,
    sortable: false,
    suppressMovable: true,
    cellClass: "center-cell",
    cellRenderer: (params) => {
      // Adding dependency on test.name to trigger re-render when it changes
      const testName = params.data.test?.name;

      return (
        <div
          style={{
            display: "flex",
            gap: "8px",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {shouldShowLabResultIcon(tableKey, testName) && (
            <img
              src={
                params.data?.result_data ? labResultActive : labResultInActive
              }
              alt="lab result icon"
              onClick={() => {
                if (params.data?.result_data && handleToggleSidebar) {
                  handleToggleSidebar(params.data);
                }
              }}
              style={{
                cursor: params.data?.result_data ? "pointer" : "default",
                width: "19px",
                height: "20px",
              }}
            />
          )}
          <img
            src={require("assets/icons/delete.png")}
            alt="delete icon"
            className="delete-icon"
            onClick={() =>
              handleDeleteRow({ row: params.data, tableKey: tableKey })
            }
            style={{ cursor: "pointer" }}
          />
        </div>
      );
    },
  }),
};

const tableSpecificColumns = {
  remediation: ({
    data,
    tableKey,
    testListOptions,
    selectedOptions,
    setSelectedOptions,
    columnNames,
  }) => ({
    sampleType: {
      headerComponent: () => (
        <CustomFilters
          header="Sample Type"
          tableKey={tableKey}
          testList={testListOptions.map((test) => test.name)}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
        />
      ),
      headerName: columnNames.sample_type.name,
      field: columnNames.sample_type.value,
      editable: true,
      flex: 2,
      minWidth: 160,
      sortable: false,
      suppressMovable: true,
      cellEditor: CustomSelectEditor,
      cellEditorParams: {
        label: "- Select Type -",
        values: testListOptions,
        tableKey,
      },
      valueGetter: (params) => params.data.test?.name || "",
      valueSetter: (params) => {
        const selectedTest = testListOptions.find(
          (test) => test.id === params.newValue.id
        );
        if (selectedTest) {
          params.data.test = {
            id: selectedTest.id,
            name: selectedTest.name,
            edit_col: true,
          };
          handleTestUpdateLogic(params.data, data, testListOptions);

          // Refresh the delete column to update the lab result icon visibility
          setTimeout(() => {
            params.api.refreshCells({
              rowNodes: [params.node],
              force: true,
            });
          }, 0);

          return true;
        }
        return false;
      },
    },
    sampleLocation: {
      headerName: columnNames.sample_location.name,
      field: columnNames.sample_location.value,
      editable: true,
      flex: 1,
      minWidth: 130,
      cellEditorParams: { type: "text", rows: 4, maxLength: 50 },
      filter: false,
      sortable: false,
      suppressMovable: true,
      valueGetter: (params) => params.data.sample_info?.[0]?.value || "",
      valueSetter: (params) => {
        const sampleInfo = params.data.sample_info?.[0];
        if (sampleInfo) {
          sampleInfo.value = params.newValue || "";
          return true;
        }
        return false;
      },
    },
    observation: {
      headerName: columnNames.observation.name,
      field: columnNames.observation.value,
      editable: true,
      flex: 1,
      minWidth: 150,
      cellEditorParams: { type: "text", rows: 4, maxLength: 50 },
      cellStyle: { textAlign: "left" },
      filter: "agTextColumnFilter",
      filterParams: { filterOptions: ["contains"], maxNumConditions: 1 },
      sortable: false,
      suppressMovable: true,
      valueSetter: (params) => {
        if (params.newValue !== params.data.observation?.name) {
          params.data.observation = {
            ...params.data.observation,
            name: params.newValue,
            edit_col: true,
          };
          handleObservationUpdateLogic(params.data, data);
          return true;
        }
        return false;
      },
    },
  }),
};

// Main column generator functions
export const getColumnsOfRemediationTable = (props) => {
  const {
    data,
    tiers,
    tableKey,
    selectedTier,
    selectAllRows,
    handleStatusRow,
    handleSelectRow,
    handleDeleteRow,
    setSelectedTier,
    testListOptions,
    selectedOptions,
    handleAllSelectRow,
    setSelectedOptions,
    handleToggleSidebar,
  } = props;

  return [
    sharedColumns.selection({
      tableKey,
      selectAllRows,
      handleSelectRow,
      handleAllSelectRow,
    }),
    sharedColumns.drag,
    sharedColumns.roomName({ data, columnNames: columnsNames }),
    tableSpecificColumns.remediation({
      data,
      tableKey,
      testListOptions,
      selectedOptions,
      setSelectedOptions,
      columnNames: columnsNames,
    }).sampleType,
    tableSpecificColumns.remediation({ columnNames: columnsNames })
      .sampleLocation,
    sharedColumns.sampleNote({ columnNames: columnsNames }),
    tableSpecificColumns.remediation({ columnNames: columnsNames, data })
      .observation,
    sharedColumns.result({ columnNames: columnsNames }),
    sharedColumns.status({
      tableKey,
      columnNames: columnsNames,
      handleStatusRow,
    }),
    sharedColumns.tier({
      tiers,
      tableKey,
      selectedTier,
      setSelectedTier,
      columnNames: columnsNames,
    }),
    sharedColumns.delete({ handleDeleteRow, tableKey, handleToggleSidebar }),
  ];
};

export const getColumnsOfHvacTable = (props) => {
  const {
    data,
    tableKey,
    selectedTier,
    selectAllRows,
    handleStatusRow,
    setSelectedTier,
    handleSelectRow,
    handleDeleteRow,
    handleAllSelectRow,
  } = props;

  return [
    sharedColumns.selection({
      tableKey,
      selectAllRows,
      handleSelectRow,
      handleAllSelectRow,
    }),
    sharedColumns.drag,
    sharedColumns.roomName({ data, columnNames: hvacColumnsNames }),
    sharedColumns.sampleNote({ columnNames: hvacColumnsNames }),
    sharedColumns.result({ columnNames: hvacColumnsNames }),
    sharedColumns.status({
      tableKey,
      columnNames: hvacColumnsNames,
      handleStatusRow,
    }),
    sharedColumns.tier({
      tiers: {
        tier: [
          { id: 1, prefix: "Clean", title: "Clean" },
          { id: 2, prefix: "Remove", title: "Remove" },
          { id: 4, prefix: "P", title: "P" },
        ],
      },
      tableKey,
      selectedTier,
      setSelectedTier,
      columnNames: hvacColumnsNames,
    }),
    sharedColumns.delete({ handleDeleteRow, tableKey }),
  ];
};

export const getColumnsOfAmbientTable = (props) => {
  const {
    data,
    tableKey,
    selectedTier,
    selectAllRows,
    handleStatusRow,
    setSelectedTier,
    handleSelectRow,
    handleDeleteRow,
    handleAllSelectRow,
  } = props;

  return [
    sharedColumns.selection({
      tableKey,
      selectAllRows,
      handleSelectRow,
      handleAllSelectRow,
    }),
    sharedColumns.drag,
    sharedColumns.roomName({ data, columnNames: ambientColumnsNames }),
    sharedColumns.sampleNote({ columnNames: ambientColumnsNames }),
    sharedColumns.result({ columnNames: ambientColumnsNames }),
    sharedColumns.status({
      columnNames: ambientColumnsNames,
      handleStatusRow,
      tableKey,
    }),
    sharedColumns.tier({
      tiers: {
        tier: [
          { id: 1, prefix: "Basic Cleaning", title: "Basic Cleaning" },
          { id: 2, prefix: "Deep Cleaning", title: "Deep Cleaning" },
          { id: 3, prefix: "Full Pack Out", title: "Full Pack Out" },
          { id: 4, prefix: "P", title: "P" },
        ],
      },
      tableKey,
      selectedTier,
      setSelectedTier,
      columnNames: ambientColumnsNames,
    }),
    sharedColumns.delete({ handleDeleteRow, tableKey }),
  ];
};

// Utility function to format values - returns value if valid, otherwise "-"
const formatValue = (value, symbol = "") => {
  if (value === null || value === undefined) {
    return symbol;
  }
  if (typeof value === "object" || Array.isArray(value)) {
    return symbol;
  }
  return value;
};

// Helper function to determine if a room is an outside room
const isOutsideRoom = (data) => {
  const roomTypeName =
    data?.room_type_name ||
    data?.room_type?.name ||
    data?.result_data?.room_type_name ||
    data?.result_data?.result_data?.room_type_name ||
    "";
  return typeof roomTypeName === "string"
    ? roomTypeName.toLowerCase() === "outside"
    : false;
};

// Helper function to get test type from room-level data only
const getRoomTestType = (data) => {
  const candidates = [
    data?.test_name,
    data?.type,
    data?.test?.name,
    data?.test_sample_name,
    data?.result_data?.test_name,
    data?.result_data?.type,
    data?.result_data?.test?.name,
    data?.result_data?.test_sample_name,
    data?.result_data?.result_data?.test_name,
    data?.result_data?.result_data?.type,
    data?.result_data?.result_data?.test?.name,
    data?.result_data?.result_data?.test_sample_name,
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());

  if (candidates.some((value) => value.includes("air"))) return "air";
  if (candidates.some((value) => value.includes("swab"))) return "swab";

  return "";
};

const getSampleResults = (data) =>
  data?.result_data?.result_data?.Sample_Results ||
  data?.result_data?.Sample_Results ||
  data?.Sample_Results ||
  [];

const getSampleMeta = (data) => ({
  observation_name:
    data?.observation_name ||
    data?.result_data?.observation_name ||
    data?.result_data?.result_data?.observation_name,
  room_name:
    data?.room_name ||
    data?.result_data?.room_name ||
    data?.result_data?.result_data?.room_name,
});

const getObservationLocation = (data, sample) =>
  data?.sample_info?.[0]?.value ||
  data?.observation_location ||
  data?.observation?.location ||
  sample?.Observation_Location ||
  sample?.Client_Sample_ID?.split(": ")[1] ||
  "";

const getSampleTestType = (sample) => {
  const candidates = [
    sample?.Media,
    sample?.Test_Type,
    sample?.test_name,
    sample?.type,
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());

  if (candidates.some((value) => value.includes("air"))) return "air";
  if (candidates.some((value) => value.includes("swab"))) return "swab";
  return "";
};

const getSampleIdentifier = (sample) => {
  const sampleId =
    sample?.Sample_ID ??
    sample?.sample_id ??
    sample?.id ??
    sample?.SampleId ??
    sample?.sampleId;
  if (sampleId === null || sampleId === undefined) {
    return null;
  }
  return String(sampleId);
};

// Test Results Table Component
export const TestResultsTable = ({ resultData, outsideRoom }) => {
  const currentSampleResults = getSampleResults(resultData);

  if (!currentSampleResults?.length) {
    return <div>No test results available</div>;
  }

  // Get current test type and room type
  const currentTestType =
    getRoomTestType(resultData) || getSampleTestType(currentSampleResults[0]);
  const currentSampleId = resultData?.id;

  const allSamples = [];
  const seenSampleIds = new Set();
  const currentMeta = getSampleMeta(resultData);

  // Step 1: Add current room's Sample_Results
  currentSampleResults.forEach((sample) => {
    const sampleTestType = getSampleTestType(sample);
    if (currentTestType === "air" && sampleTestType !== "air") {
      return;
    }
    const sampleIdentifier = getSampleIdentifier(sample);
    if (!sampleIdentifier) {
      return;
    }
    if (!seenSampleIds.has(sampleIdentifier)) {
      allSamples.push({
        ...sample,
        observation_name: currentMeta.observation_name,
        room_name: currentMeta.room_name,
      });
      seenSampleIds.add(sampleIdentifier);
    }
  });

  // Step 2: Process outsideRoom array based on current test type
  if (currentTestType === "air" && outsideRoom?.length) {
    // For AIR tests: Add all outside room AIR tests (but not the current one again)
    outsideRoom.forEach((room) => {
      const roomTestType = getRoomTestType(room);
      const roomIsOutside = isOutsideRoom(room);
      const roomSampleId = room.id;

      // Skip if this is the same sample we already added
      if (roomSampleId === currentSampleId) {
        return;
      }

      // Only add outside AIR tests
      if (roomIsOutside && roomTestType === "air") {
        const roomSamples = getSampleResults(room);
        if (roomSamples?.length) {
          const roomMeta = getSampleMeta(room);
          roomSamples.forEach((sample) => {
            const sampleTestType = getSampleTestType(sample);
            if (sampleTestType !== "air") {
              return;
            }
            const sampleIdentifier = getSampleIdentifier(sample);
            if (!sampleIdentifier) {
              return;
            }
            if (!seenSampleIds.has(sampleIdentifier)) {
              allSamples.push({
                ...sample,
                observation_name: roomMeta.observation_name,
                room_name: roomMeta.room_name,
              });
              seenSampleIds.add(sampleIdentifier);
            }
          });
        }
      }
    });
  }
  // For SWAB tests: Don't add any outside room samples (only current room shown)

  const sampleResults = allSamples;

  return (
    <div className="test-results-container">
      {/* Sample Results - Side by Side */}
      <div className="samples-grid">
        {sampleResults.map((sample, index) => (
          <div
            key={index}
            className="sample-result-column"
            style={{ borderLeft: index ? "1px solid #000000" : "none" }}
          >
            {/* Sample Header Info */}
            <div className="sample-header-info">
              <div className="info-item">
                <span className="info-label">Sample ID:</span>
                <span className="info-value">
                  {formatValue(sample.Analysis_Id || sample.Sample_ID)}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Client Sample ID:</span>
                <span className="info-value">
                  {formatValue(sample.Client_Sample_ID)}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Room Name:</span>
                <span className="info-value">
                  {typeof sample?.room_name === "object" &&
                  sample?.room_name?.name
                    ? formatValue(sample.room_name.name)
                    : formatValue(sample?.room_name)}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Observation Name:</span>
                <span className="info-value">
                  {typeof sample?.observation_name === "object" &&
                  sample?.observation_name?.name
                    ? formatValue(sample.observation_name.name)
                    : formatValue(sample?.observation_name)}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Sample location:</span>
                <span className="info-value">
                  {formatValue(getObservationLocation(resultData, sample))}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">
                  {currentTestType === "swab"
                    ? "Area Swabbed:"
                    : "Volume Sampled(L):"}
                </span>
                <span className="info-value">
                  {currentTestType === "swab"
                    ? formatValue(sample.Area_Swabbed)
                    : formatValue(sample.Volume_Sampled_L)}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Media:</span>
                <span className="info-value">
                  {currentTestType === "swab"
                    ? "Swab Sample"
                    : formatValue(sample.Media)}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">
                  {currentTestType === "swab"
                    ? "Sample Analysis:"
                    : "Percent of Trace Analyzed:"}
                </span>
                <span className="info-value">
                  {formatValue(sample.Percent_of_Trace_Analyzed)}
                </span>
              </div>
            </div>

            {/* Spore Data Table */}
            {sample.Spore_Data && (
              <div className="spore-data-table-container">
                <table className="spore-data-table">
                  <thead>
                    <tr>
                      <th>Spore Types</th>
                      <th>Raw Count</th>
                      <th>
                        {currentTestType === "swab" ? "Count/cm2" : "Count/m3"}
                      </th>
                      <th>%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(sample.Spore_Data)
                      .sort((a, b) => a[0].localeCompare(b[0]))
                      .map(([sporeType, data]) => {
                        const rawCount = data?.Raw_Count;
                        const countPerM =
                          currentTestType === "swab"
                            ? data?.Count_per_cm2
                            : data?.Count_per_m3;
                        const percent = data?.Percent;

                        return (
                          <tr key={sporeType}>
                            <td>{sporeType}</td>
                            <td>{formatValue(rawCount)}</td>
                            <td>{formatValue(countPerM)}</td>
                            <td>{formatValue(percent)}</td>
                          </tr>
                        );
                      })}

                    <tr style={{ backgroundColor: "#E8F9D2" }}>
                      <td>TOTAL spores</td>
                      <td>
                        {formatValue(
                          sample.Totals?.TOTAL_spores?.Raw_Count,
                          "-"
                        )}
                      </td>
                      <td>
                        {currentTestType === "air" &&
                          formatValue(
                            sample.Totals?.TOTAL_spores?.Count_per_m3,
                            "-"
                          )}
                        {currentTestType === "swab" &&
                          formatValue(
                            sample.Totals?.TOTAL_spores?.Count_per_cm2,
                            "-"
                          )}
                      </td>
                      <td></td>
                    </tr>

                    <tr className="spore-data-table-border-top">
                      <td>Hyphal fragments</td>
                      <td>
                        {formatValue(
                          sample.Totals?.Hyphal_fragments?.Raw_Count,
                          "-"
                        )}
                      </td>
                      <td>
                        {currentTestType === "air" &&
                          formatValue(
                            sample.Totals?.Hyphal_fragments?.Count_per_m3,
                            "-"
                          )}
                        {currentTestType === "swab" &&
                          formatValue(
                            sample.Totals?.Hyphal_fragments?.Count_per_cm2,
                            "-"
                          )}
                      </td>
                      <td></td>
                    </tr>
                    {currentTestType === "swab" && (
                      <tr className="spore-data-table-border-top">
                        <td>Detection Limit</td>
                        <td>
                          {formatValue(
                            sample.Totals?.Detection_Limit?.Raw_Count,
                            "-"
                          )}
                        </td>
                        <td>
                          {formatValue(
                            sample.Totals?.Detection_Limit?.Count_per_cm2,
                            "-"
                          )}
                        </td>
                        <td></td>
                      </tr>
                    )}
                    {currentTestType === "air" && (
                      <tr className="spore-data-table-border-top">
                        <td>Pollen</td>
                        <td>
                          {formatValue(sample.Totals?.Pollen?.Raw_Count, "-")}
                        </td>
                        <td>
                          {formatValue(
                            sample.Totals?.Pollen?.Count_per_m3,
                            "-"
                          )}
                        </td>
                        <td></td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {sample.Totals?.Background_debris !== undefined && (
                  <div className="sample-total-bottom">
                    Background debris
                    <span>{formatValue(sample.Totals.Background_debris)}</span>
                  </div>
                )}

                {sample.Totals?.Analytical_Sensitivity !== undefined && (
                  <div className="sample-total-bottom">
                    Analytical Sensitivity
                    <span>
                      {formatValue(sample.Totals.Analytical_Sensitivity)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
